import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ccmService from '../../services/ccmService';
import './CarePlans.css';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';

const PRIORITY_OPTIONS = ['high', 'medium', 'low'];
const STATUS_OPTIONS = ['not_started', 'in_progress', 'met', 'discontinued'];

const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  met: 'Met',
  discontinued: 'Discontinued',
};

const PRIORITY_COLORS = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};

const STATUS_COLORS = {
  draft: { bg: '#fef3c7', color: '#92400e', label: 'Draft' },
  active: { bg: '#d1fae5', color: '#065f46', label: 'Active' },
  archived: { bg: '#f3f4f6', color: '#6b7280', label: 'Archived' },
};

const emptyGoal = () => ({
  goal_description: '',
  objectives: [],
  interventions: [],
  priority: 'medium',
  status: 'not_started',
  target_date: '',
  _objInput: '',
  _intvInput: '',
});

// ─── Small reusable chip list with inline add ───────────────────────────────
function ChipList({ items, inputVal, onInputChange, onAdd, onRemove, placeholder, color }) {
  const inputRef = useRef(null);
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onAdd(); }
  };
  return (
    <div className="cp-chip-area">
      <div className="cp-chips">
        {items.map((item, i) => (
          <span key={i} className="cp-chip" style={{ '--chip-color': color }}>
            {item}
            <button type="button" onClick={() => onRemove(i)} className="cp-chip-remove">×</button>
          </span>
        ))}
      </div>
      <div className="cp-chip-input-row">
        <input
          ref={inputRef}
          type="text"
          className="cp-chip-input"
          placeholder={placeholder}
          value={inputVal}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button type="button" className="cp-chip-add-btn" onClick={onAdd}>+ Add</button>
      </div>
    </div>
  );
}

// ─── Single Goal Card ────────────────────────────────────────────────────────
function GoalCard({ goal, index, onChange, onRemove, isOnly }) {
  const updateField = (field, value) => onChange(index, field, value);

  const addItem = (field, inputField) => {
    const val = (goal[inputField] || '').trim();
    if (!val) return;
    onChange(index, field, [...(goal[field] || []), val]);
    onChange(index, inputField, '');
  };
  const removeItem = (field, i) => {
    const updated = [...(goal[field] || [])];
    updated.splice(i, 1);
    onChange(index, field, updated);
  };

  return (
    <div className="cp-goal-card">
      <div className="cp-goal-card-header">
        <div className="cp-goal-number">
          <span className="cp-goal-num-badge">#{index + 1}</span>
          <span className="cp-goal-title-hint">Problem / Goal</span>
        </div>
        <div className="cp-goal-header-right">
          <select
            className="cp-select cp-priority-select"
            value={goal.priority}
            onChange={(e) => updateField('priority', e.target.value)}
            style={{ '--pri-color': PRIORITY_COLORS[goal.priority] }}
          >
            {PRIORITY_OPTIONS.map(p => (
              <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)} Priority</option>
            ))}
          </select>
          {!isOnly && (
            <button type="button" className="cp-remove-goal-btn" onClick={() => onRemove(index)}>
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="cp-goal-body">
        {/* Goal Description */}
        <div className="cp-form-group cp-form-group--full">
          <label className="cp-label cp-label--required">Goal Description</label>
          <textarea
            className="cp-textarea"
            rows={2}
            placeholder="e.g. Reduce HbA1c below 7% within 3 months through diet and medication adherence"
            value={goal.goal_description}
            onChange={(e) => updateField('goal_description', e.target.value)}
          />
        </div>

        <div className="cp-goal-columns">
          {/* Objectives */}
          <div className="cp-form-group">
            <label className="cp-label">
              <span className="cp-label-dot" style={{ background: '#6366f1' }} />
              Objectives <span className="cp-label-hint">(measurable milestones)</span>
            </label>
            <ChipList
              items={goal.objectives || []}
              inputVal={goal._objInput || ''}
              onInputChange={(v) => updateField('_objInput', v)}
              onAdd={() => addItem('objectives', '_objInput')}
              onRemove={(i) => removeItem('objectives', i)}
              placeholder="e.g. Check blood sugar twice daily"
              color="#6366f1"
            />
          </div>

          {/* Interventions */}
          <div className="cp-form-group">
            <label className="cp-label">
              <span className="cp-label-dot" style={{ background: '#0ea5e9' }} />
              Interventions <span className="cp-label-hint">(clinical actions)</span>
            </label>
            <ChipList
              items={goal.interventions || []}
              inputVal={goal._intvInput || ''}
              onInputChange={(v) => updateField('_intvInput', v)}
              onAdd={() => addItem('interventions', '_intvInput')}
              onRemove={(i) => removeItem('interventions', i)}
              placeholder="e.g. Educate patient on low-carb diet"
              color="#0ea5e9"
            />
          </div>
        </div>

        <div className="cp-goal-footer-row">
          <div className="cp-form-group cp-form-group--inline">
            <label className="cp-label">Target Date</label>
            <AppDateInput
              className="cp-input"
              value={goal.target_date || ''}
              onChange={(v) => updateField('target_date', v)}
            />
          </div>
          <div className="cp-form-group cp-form-group--inline">
            <label className="cp-label">Status</label>
            <select
              className="cp-select"
              value={goal.status}
              onChange={(e) => updateField('status', e.target.value)}
            >
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Care Plan View (read-only) ───────────────────────────────────────────────
function CarePlanView({ plan, onApprove }) {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS_COLORS[plan.status] || STATUS_COLORS.draft;

  return (
    <div className="cp-plan-card">
      <div className="cp-plan-card-header" onClick={() => setExpanded(e => !e)}>
        <div className="cp-plan-card-left">
          <span className="cp-plan-status-dot" style={{ background: st.color }} />
          <div>
            <h4 className="cp-plan-name">{plan.plan_name}</h4>
            <p className="cp-plan-meta">
              Created {new Date(plan.created_date).toLocaleDateString()} &nbsp;·&nbsp;
              {plan.goals?.length ?? 0} goal{plan.goals?.length !== 1 ? 's' : ''}
              {plan.next_review_date && (
                <> &nbsp;·&nbsp; Next review: {new Date(plan.next_review_date).toLocaleDateString()}</>
              )}
            </p>
          </div>
        </div>
        <div className="cp-plan-card-right">
          <span className="cp-status-pill" style={{ background: st.bg, color: st.color }}>
            {st.label}
          </span>
          {plan.status === 'draft' && (
            <button
              type="button"
              className="cp-approve-btn"
              onClick={(e) => { e.stopPropagation(); onApprove(plan.id); }}
            >
              ✓ Sign & Approve
            </button>
          )}
          <span className="cp-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="cp-plan-body">
          {plan.goals && plan.goals.length > 0 ? (
            plan.goals.map((g, idx) => (
              <div key={g.id || idx} className="cp-goal-view">
                <div className="cp-goal-view-header">
                  <span
                    className="cp-priority-pill"
                    style={{ background: `${PRIORITY_COLORS[g.priority]}22`, color: PRIORITY_COLORS[g.priority] }}
                  >
                    {g.priority?.toUpperCase() || 'MEDIUM'}
                  </span>
                  <span className="cp-goal-view-desc">{g.goal_description}</span>
                  <span className="cp-goal-status-badge">{STATUS_LABELS[g.status] || g.status}</span>
                </div>
                <div className="cp-goal-view-body">
                  <div className="cp-goal-view-col">
                    <h6 className="cp-goal-view-col-title" style={{ color: '#6366f1' }}>Objectives</h6>
                    {g.objectives?.length > 0 ? (
                      <ul className="cp-goal-list">
                        {g.objectives.map((o, i) => <li key={i}>{o}</li>)}
                      </ul>
                    ) : <p className="cp-empty-hint">None specified</p>}
                  </div>
                  <div className="cp-goal-view-col">
                    <h6 className="cp-goal-view-col-title" style={{ color: '#0ea5e9' }}>Interventions</h6>
                    {g.interventions?.length > 0 ? (
                      <ul className="cp-goal-list">
                        {g.interventions.map((v, i) => <li key={i}>{v}</li>)}
                      </ul>
                    ) : <p className="cp-empty-hint">None specified</p>}
                  </div>
                  {g.target_date && (
                    <div className="cp-goal-view-col cp-goal-view-col--narrow">
                      <h6 className="cp-goal-view-col-title">Target Date</h6>
                      <p className="cp-goal-date">{new Date(g.target_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="cp-empty-hint cp-empty-hint--center">No goals documented for this plan.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const CarePlans = () => {
  const { enrollmentId } = useParams();
  const [searchParams] = useSearchParams();
  const virtualVisitId = searchParams.get('visitId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [carePlans, setCarePlans] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    plan_name: '',
    next_review_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    goals: [emptyGoal()],
  });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    if (enrollmentId) fetchCarePlans();
    else setLoading(false);
  }, [enrollmentId]);

  const fetchCarePlans = async () => {
    try {
      setLoading(true);
      const res = await ccmService.getCarePlans(enrollmentId);
      if (res.success) {
        const plans = res.data.map(p => ({
          ...p,
          goals: (p.goals || []).map(g => ({
            ...g,
            objectives: typeof g.objectives === 'string' ? JSON.parse(g.objectives || '[]') : g.objectives || [],
            interventions: typeof g.interventions === 'string' ? JSON.parse(g.interventions || '[]') : g.interventions || [],
          })),
        }));
        setCarePlans(plans);
      }
    } catch {
      showToast('Failed to load care plans', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── goal helpers ──
  const updateGoalField = (idx, field, value) => {
    setForm(f => {
      const goals = [...f.goals];
      goals[idx] = { ...goals[idx], [field]: value };
      return { ...f, goals };
    });
  };

  const addGoal = () => setForm(f => ({ ...f, goals: [...f.goals, emptyGoal()] }));
  const removeGoal = (idx) => setForm(f => ({ ...f, goals: f.goals.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!form.plan_name.trim()) { showToast('Please enter a plan title', 'error'); return; }
    const invalidGoals = form.goals.filter(g => !g.goal_description.trim());
    if (invalidGoals.length > 0) { showToast('All goals must have a description', 'error'); return; }

    setSaving(true);
    try {
      const payload = {
        enrollment_id: enrollmentId,
        virtual_visit_id: virtualVisitId || null,
        plan_name: form.plan_name.trim(),
        plan_content: form.plan_name.trim(),
        next_review_date: form.next_review_date || null,
        goals: form.goals.map(g => ({
          goal_description: g.goal_description.trim(),
          objectives: g.objectives || [],
          interventions: g.interventions || [],
          priority: g.priority,
          status: g.status,
          target_date: g.target_date || null,
        })),
      };
      const res = await ccmService.createCarePlan(payload);
      if (res.success) {
        showToast('Care plan created successfully');
        setShowBuilder(false);
        setForm({
          plan_name: '',
          next_review_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          goals: [emptyGoal()],
        });
        fetchCarePlans();
      } else {
        showToast(res.message || 'Failed to save care plan', 'error');
      }
    } catch {
      showToast('An error occurred while saving', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (carePlanId) => {
    if (!window.confirm('Sign and approve this care plan? This will archive any other active plans.')) return;
    try {
      const res = await ccmService.approveCarePlan(carePlanId);
      if (res.success) { showToast('Care plan approved and signed'); fetchCarePlans(); }
      else showToast(res.message || 'Failed to approve', 'error');
    } catch {
      showToast('An error occurred', 'error');
    }
  };

  // ── render ──
  return (
    <div className="cp-root">
      {/* Toast */}
      {toast && (
        <div className={`cp-toast cp-toast--${toast.type}`}>{toast.msg}</div>
      )}

      {/* Page header */}
      <div className="cp-page-header">
        <div className="cp-page-header-left">
          <button className="cp-back-btn" onClick={() => navigate(`/ccm/phm-hub/${enrollmentId}`)}>
            ← Back
          </button>
          <div>
            <h2 className="cp-page-title">Comprehensive Care Plans</h2>
            {virtualVisitId && (
              <span className="cp-visit-badge">Linked to Visit #{virtualVisitId}</span>
            )}
          </div>
        </div>
        {!showBuilder && enrollmentId && (
          <button className="cp-primary-btn" onClick={() => setShowBuilder(true)}>
            + Document New Care Plan
          </button>
        )}
      </div>

      {loading ? (
        <div className="cp-loading">
          <div className="cp-spinner" />
          <p>Loading care plans…</p>
        </div>
      ) : !enrollmentId ? (
        <div className="cp-empty-state">
          <span className="cp-empty-icon">📋</span>
          <h3>No Enrollment Selected</h3>
          <p>Please navigate to a patient's CCM enrollment to view care plans.</p>
        </div>
      ) : showBuilder ? (
        /* ══════════ BUILDER ══════════ */
        <div className="cp-builder">
          <div className="cp-builder-header">
            <h3 className="cp-builder-title">New Care Plan</h3>
            <button className="cp-cancel-btn" onClick={() => setShowBuilder(false)}>Cancel</button>
          </div>

          {/* Plan meta */}
          <div className="cp-builder-meta">
            <div className="cp-form-group">
              <label className="cp-label cp-label--required">Plan Title</label>
              <input
                type="text"
                className="cp-input"
                placeholder="e.g. Comprehensive CCM Care Plan — June 2026"
                value={form.plan_name}
                onChange={(e) => setForm(f => ({ ...f, plan_name: e.target.value }))}
              />
            </div>
            <div className="cp-form-group">
              <label className="cp-label">Next Review Date</label>
              <AppDateInput
                className="cp-input"
                value={form.next_review_date}
                onChange={(v) => setForm(f => ({ ...f, next_review_date: v }))}
              />
            </div>
          </div>

          {/* Goals section */}
          <div className="cp-builder-goals">
            <div className="cp-goals-header">
              <h4 className="cp-goals-title">Problems & Goals <span className="cp-goals-count">{form.goals.length}</span></h4>
              <p className="cp-goals-hint">Each problem should have a corresponding goal, with measurable objectives and planned interventions.</p>
            </div>

            {form.goals.map((goal, idx) => (
              <GoalCard
                key={idx}
                goal={goal}
                index={idx}
                onChange={updateGoalField}
                onRemove={removeGoal}
                isOnly={form.goals.length === 1}
              />
            ))}

            <button type="button" className="cp-add-goal-btn" onClick={addGoal}>
              <span className="cp-add-goal-icon">+</span> Add Another Problem / Goal
            </button>
          </div>

          {/* Footer actions */}
          <div className="cp-builder-footer">
            <button className="cp-cancel-btn" onClick={() => setShowBuilder(false)}>Cancel</button>
            <button className="cp-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save as Draft'}
            </button>
          </div>
        </div>
      ) : (
        /* ══════════ PLANS LIST ══════════ */
        <div className="cp-list">
          {carePlans.length === 0 ? (
            <div className="cp-empty-state">
              <span className="cp-empty-icon">📝</span>
              <h3>No Care Plans Documented</h3>
              <p>Click <strong>"Document New Care Plan"</strong> above to create the first care plan for this patient.</p>
            </div>
          ) : (
            carePlans.map(plan => (
              <CarePlanView key={plan.id} plan={plan} onApprove={handleApprove} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CarePlans;
